import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useLocation} from 'react-router-dom';

import useFilters from '@/hooks/useFilters';
import {merchantTransactionService} from '@/services/merchants/transactionService';
import type {AfriexQueryParams, AfriexTransactionStatus} from '@/types';
import {AfriexTransactionChannel, AfriexTransactionProcessor} from '@/types';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {formatAdminBusinessTransaction} from '@/utils/dashboard';
import {logRejection} from '@/utils/logRejection';

import {
  readFiltersFromUrl,
  toPanelFilters,
  writeFiltersToUrl,
} from './filtersInUrl';
import type {MerchantTransactionListProps} from './types';

const TAB_TRANSACTION_TYPE_MAP = {
  pool: {
    processor: AfriexTransactionProcessor.SYSTEM,
    channel: AfriexTransactionChannel.VIRTUAL_BANK_ACCOUNT,
  },
  swift: {
    processor: AfriexTransactionProcessor.SYSTEM,
    channel: AfriexTransactionChannel.SWIFT,
  },
  system: {
    processor: AfriexTransactionProcessor.SYSTEM,
    // Only the Bank Account and Mobile Money rails belong on the System tab.
    // The admin endpoint matches `channel` against either transaction leg, so a
    // narrow positive allowlist keeps SWIFT, Pool, and wallet/internal legs off
    // this tab.
    channel: [
      AfriexTransactionChannel.BANK_ACCOUNT,
      AfriexTransactionChannel.MOBILE_MONEY,
    ].join(','),
  },
};
type TabTransactionType = keyof typeof TAB_TRANSACTION_TYPE_MAP;

const useMerchantTransactionList = (): MerchantTransactionListProps => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') as AfriexTransactionStatus;

  const [transactionList, setTransactionList] = useState<
    AfriexBusinessTransaction[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearch] = useState<boolean>(false);
  const [transactionTotal, setTransactionTotal] = useState<number>(0);
  const [showPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  // Seeded from the URL, so Back from a transaction restores the filters.
  const [restoredFilters] = useState(() => readFiltersFromUrl(location.search));
  const [currentFilters, setCurrentFilters] =
    useState<AfriexQueryParams>(restoredFilters);
  const [exportFilters, setExportFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>(() =>
    toPanelFilters(restoredFilters),
  );
  const [filterCount, setFilterCount] = useState(
    () => Object.values(toPanelFilters(restoredFilters)).filter(Boolean).length,
  );

  useEffect(() => {
    writeFiltersToUrl(currentFilters);
  }, [currentFilters]);
  const [tabType, setTabType] = useState<string>('all');
  const currentFiltersRef = useRef<AfriexQueryParams>({});
  const fetchIdRef = useRef<number>(0);
  // Query of the request in flight, so an identical one is not sent twice.
  const inFlightKeyRef = useRef<string | null>(null);
  const {applyFilters} = useFilters(setCurrentFilters);

  // The `status` URL param is layered onto the filters the request is built
  // from rather than mirrored into `currentFilters` state by an effect. That
  // effect ran after the first render and always handed `currentFilters` a
  // fresh object identity, which refired the fetch effect below and sent a
  // second, identical request on every mount (the `fetchIdRef` guard threw the
  // stale response away, so the UI never flickered, but the request was real).
  // `currentFilters` now holds only the filters the admin picked, so its
  // identity is stable and this memo is stable with it.
  const effectiveFilters = useMemo(
    () => (status ? {...currentFilters, status} : currentFilters),
    [currentFilters, status],
  );

  // The filters every request is built from: the admin's plus the tab's.
  const activeFilters = useMemo(
    () => ({
      ...effectiveFilters,
      ...TAB_TRANSACTION_TYPE_MAP[tabType as TabTransactionType],
    }),
    [effectiveFilters, tabType],
  );

  // A layout effect, so the ref is current before any child's effects run:
  // Pagination fetches from its own mount/reset effect through
  // handleFetchTransactions, and used to send the previous tab's filters.
  useLayoutEffect(() => {
    currentFiltersRef.current = activeFilters;
  }, [activeFilters]);

  const fetchAllTransactions = useCallback(
    async (params?: AfriexQueryParams) => {
      // Pagination fetches page 0 when it mounts or resets, in the same
      // commit as the filter/tab effect below; skip whichever arrives second.
      const key = JSON.stringify({page: 0, limit: 10, ...params});
      if (key === inFlightKeyRef.current) {
        return;
      }
      inFlightKeyRef.current = key;
      try {
        setError(null);
        setIsLoading(true);
        const currentFetchId = ++fetchIdRef.current;
        let transactionData;
        try {
          transactionData =
            await merchantTransactionService.getBusinessTransactionList(
              params ?? {},
            );
        } finally {
          if (inFlightKeyRef.current === key) {
            inFlightKeyRef.current = null;
          }
        }

        if (currentFetchId !== fetchIdRef.current) {
          return;
        }

        const {page: _page, limit: _limit, ...filters} = params ?? {};
        setExportFilters(filters);

        const filtered = await Promise.all(
          transactionData?.data &&
            transactionData?.data.map(async (transaction) => {
              const at = await formatAdminBusinessTransaction(transaction);
              return {
                ...transaction,
                type: at.type,
                adminUser: at.adminUser,
              };
            }),
        );
        setTransactionList(filtered);
        setTransactionTotal(transactionData?.total || 0);
        setIsLoading(false);
      } catch (error) {
        setError(error);
        setIsLoading(false);
      }
    },
    [],
  );

  // `MerchantTransactionListProps.fetchTransactionList` is
  // `(params) => void` (types.ts), and `handleFetchTransactions` below is a
  // `=> void` callback too, so adapt at the boundary rather than widening
  // either contract. `fetchAllTransactions` catches into `error` state, so this
  // handler only fires if that catch itself throws. `useCallback` keeps the
  // identity stable for the memoised callbacks that depend on it.
  const fetchTransactionList = useCallback(
    (params?: AfriexQueryParams) => {
      fetchAllTransactions(params).catch(
        logRejection('Failed to load merchant transactions'),
      );
    },
    [fetchAllTransactions],
  );

  // A tab id selects the tab (the effect below fetches it); an object is a
  // page request from Pagination, sent with the current tab and filters.
  const handleFetchTransactions = useCallback(
    (typeOrParams: string | AfriexQueryParams) => {
      if (typeof typeOrParams === 'string') {
        setTabType(typeOrParams);
        return;
      }
      fetchTransactionList({...typeOrParams, ...currentFiltersRef.current});
    },
    [fetchTransactionList],
  );

  // One fetch per change of tab or filters. Switching tab also clears the
  // filters, and both updates land in the same render.
  /* eslint-disable react-hooks/set-state-in-effect -- fetchTransactionList drives isLoading/error state as the fetch runs, not a render-phase set-state */
  useEffect(() => {
    fetchTransactionList(activeFilters);
  }, [activeFilters, fetchTransactionList]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleApplyFilters = useCallback(() => {
    const filters = status
      ? {...filtersToApply, status} // Ensure status is included
      : filtersToApply;
    const applied = applyFilters(filters);
    // Pagination reset is success-only work. applyFilters returns false when it
    // rejects a date range, and Pagination reacts to this flag by calling
    // onPageChange -> a refetch, so toggling it unconditionally fired a request
    // the user never asked for. See hooks/useFilters.ts.
    if (!applied) {
      return false;
    }

    setIsPageReset((prev) => !prev);
    return true;
  }, [applyFilters, filtersToApply, status]);

  // Handle change for user manipulation
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  // Handle dates for applied filters
  const selectAppliedFilters = useCallback(
    (name: string, value: string | number | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Count only filters that are actually set. Counting `!== undefined`
        // kept an emptied text field (which holds '') in the tally, so Apply
        // stayed enabled and the label read `Apply Filters (1)` with nothing
        // filled in. This is the same emptiness rule `useFilters.applyFilters`
        // already applies one layer down, so the count and the applied set now
        // agree. No filter value is a meaningful 0.
        const filteredCount =
          Object.values(updatedFilters).filter(Boolean).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = useCallback(() => {
    // Keep the same object when there is nothing to clear, so a tab switch
    // with no filters set does not also count as a filter change. The URL
    // `status` is layered on by effectiveFilters, not held here.
    setCurrentFilters((prev) => (Object.keys(prev).length ? {} : prev));
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  }, []);

  return {
    error,
    fetchTransactionList,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: transactionTotal,
    },
    showPagination,
    transactionList,
    exportFilters,
    filterCount,
    isPageReset,
    currentFilters,
    filtersToApply,
    clearFilters: cleanFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
    handleFetchTransactions,
  };
};

export default useMerchantTransactionList;
